import { InlineCheckboxListControl, InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { OntarioObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { BaseOntarioObstetricalFileSubSection, BaseOntarioObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/Subcomponent/BaseOntarioObstetricalFileSubSection";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { ActivityPlanning } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/ActivityPlanning";
import { InfantFeedingType } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/InfantFeedingType";
import { VaginalExperience } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/VaginalExperience";

class OntarioObstetricalFilePostnatalHistoryInfant extends BaseOntarioObstetricalFileSubSection<BaseOntarioObstetricalFileSubSectionProps, never> {
    private readonly reviewOfBirthDetailsId = "PostnatalReviewOfBirthDetails";
    private readonly babysHealthConcernsId = "PostnatalBabysHealthConcerns";
    private readonly feedingConcernsId = "PostnatalFeedingConcerns";

    public render(): JSX.Element {
        const postNatal = this.props.form.Postnatal;
        const fieldLimits = OntarioObstetricalFileFieldLimits.Page5;

        return (
            <div>
                <div className="flex-display flex-wrap flex-start">
                    <div className="margin-lg-right bold margin-md-bottom">
                        {this.getString("ObstetricalFile_ReviewOfBirthLabel")}
                    </div>
                    <div>
                        <div className="flex-display margin-md-bottom">
                            <div className="align-right margin-md-right" style={{ width: "100px" }}>
                                <label className="bold margin-none">{this.getString("VaginalLabel")}</label>
                            </div>
                            <InlineCheckboxListControl
                                id="vaginal-birth-review"
                                values={postNatal.History.VaginalBirthExperienceItem}
                                onChange={values => this.props.mutateModelStateWithFormUpdate(p => p.Postnatal.History.VaginalBirthExperienceItem = values)}
                                items={this.vaginalBirthReviewOptions}
                                disabled={this.props.readonly}
                            />
                        </div>
                        <div className="flex-display">
                            <div className="align-right margin-md-right" style={{ width: "100px" }}>
                                <label className="bold margin-none">{this.getString("CaesareanLabel")}</label>
                            </div>
                            <InlineOptionsListControl
                                id="caeserean-birth-review"
                                value={postNatal.History.CaesareanBirthExperience}
                                onChange={option => this.props.mutateModelStateWithFormUpdate(p => p.Postnatal.History.CaesareanBirthExperience = option?.Key)}
                                items={this.caesareanBirthReviewOptions}
                                allowDeselect
                                disabled={this.props.readonly}
                            />
                        </div>
                    </div>
                </div>
                <div className="row row-form">
                    {this.renderSimpleField(
                        "DetailsLabel",
                        this.controlRenderer.renderTextarea({
                            value: postNatal.History.Details,
                            onChange: value => this.props.mutateModelState(s => s.Postnatal.History.Details = value),
                            maxLength: fieldLimits.ReviewOfBirth_Details.MaxChars,
                            maxRows: fieldLimits.ReviewOfBirth_Details.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(this.reviewOfBirthDetailsId, OntarioObstetricalFileClickableTextContext.Postnatal_HistoryDetailsComment),
                            id: this.reviewOfBirthDetailsId
                        }),
                        "col-md-8"
                    )}
                    {this.renderSimpleField(
                        "BirthAttendantLabel",
                        this.controlRenderer.renderPhysicianSearchTextbox(
                            postNatal.History.BirthAttendant,
                            (attendant, isSelected) => this.props.mutateModelState(
                                s => s.Postnatal.History.BirthAttendant = attendant,
                                () => {
                                    if (isSelected)
                                        this.props.handleFormUpdate();
                                }
                            ),
                            "",
                            "Postnatal_BirthAttendant",
                            fieldLimits.Postnatal_BirthAttendant.MaxChars
                        ),
                        "col-md-4"
                    )}
                </div>
                <div className="row row-form">
                    {this.renderSimpleField(
                        "ObstetricalFile_BirthIssuesRequiringFollowUpLabel",
                        this.controlRenderer.renderTextbox({
                            value: postNatal.History.PregnancyOrBirthIssuesRequiringFollowUp,
                            onChange: value => this.props.mutateModelState(s => s.Postnatal.History.PregnancyOrBirthIssuesRequiringFollowUp = value),
                            maxLength: fieldLimits.PregnancyBirthIssuesRequiringFollowUp.MaxChars
                        }),
                        "col-md-6"
                    )}
                    {this.renderSimpleField(
                        "ObstetricalFile_BabysNameLabel",
                        this.controlRenderer.renderTextbox({
                            value: postNatal.History.BabyName,
                            onChange: value => this.props.mutateModelState(s => s.Postnatal.History.BabyName = value),
                            maxLength: fieldLimits.BabyName.MaxChars
                        }),
                        "col-md-3"
                    )}
                    {this.renderSimpleField(
                        "ObstetricalFile_BabysCareProviderLabel",
                        this.controlRenderer.renderPhysicianSearchTextbox(
                            postNatal.History.BabyCareProvider,
                            (careProvider, isSelected) => this.props.mutateModelState(
                                s => s.Postnatal.History.BabyCareProvider = careProvider,
                                () => {
                                    if (isSelected)
                                        this.props.handleFormUpdate();
                                }
                            ),
                            "",
                            "PostnatalBabyCareProvider",
                            fieldLimits.BabyCareProvider.MaxChars
                        ),
                        "col-md-3"
                    )}
                </div>
                <div className="row row-form">
                    {this.renderSimpleField(
                        "BirthWeighGramsLabel",
                        this.controlRenderer.renderTextbox({
                            value: postNatal.History.BirthWeight,
                            onChange: value => this.props.mutateModelState(s => s.Postnatal.History.BirthWeight = value),
                            isNumeric: true,
                            maxLength: 5
                        }),
                        "col-md-2"
                    )}
                    {this.renderSimpleField(
                        "ObstetricalFile_BabysHealthConcernsLabel",
                        this.controlRenderer.renderTextbox({
                            value: postNatal.History.BabyHealthOrConcerns,
                            onChange: value => this.props.mutateModelState(s => s.Postnatal.History.BabyHealthOrConcerns = value),
                            id: this.babysHealthConcernsId,
                            maxLength: fieldLimits.BabyHealthConcerns.MaxChars,
                            clickableTextOptions: this.getClickableTextOptions(this.babysHealthConcernsId, OntarioObstetricalFileClickableTextContext.Postnatal_BabyHealthConcern)
                        }),
                        "col-md-10"
                    )}
                </div>
                <div className="row row-form">
                    <div className="col-md-12">
                        <label className="bold">{this.getString("InfantFeedingLabel")}</label>
                        <InlineOptionsListControl
                            id="infant-feeding-type"
                            value={postNatal.History.InfantFeeding}
                            onChange={value => this.props.mutateModelStateWithFormUpdate(p => p.Postnatal.History.InfantFeeding = value?.Key)}
                            items={this.infantFeedingTypeOptions}
                            allowDeselect
                            disabled={this.props.readonly}
                        />
                    </div>
                </div>
                <div className="row row-form">
                    {this.renderSimpleField(
                        "FeedingConcernsLabel",
                        this.controlRenderer.renderTextarea({
                            value: postNatal.History.FeedingConcerns,
                            onChange: value => this.props.mutateModelState(s => s.Postnatal.History.FeedingConcerns = value),
                            id: this.feedingConcernsId,
                            maxLength: fieldLimits.FeedingConcerns.MaxChars,
                            maxRows: fieldLimits.FeedingConcerns.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(this.feedingConcernsId, OntarioObstetricalFileClickableTextContext.Postnatal_FeedingConcerns),
                            className: "feeding-concern"
                        }),
                        "col-md-12"
                    )}
                </div>
            </div>
        );
    }

    public get vaginalBirthReviewOptions(): InlineOption<VaginalExperience>[] {
        return [
            new InlineOption(VaginalExperience.Spontaneous, this.getString("SpontaneousLabel")),
            new InlineOption(VaginalExperience.Vacuum, this.getString("VacuumLabel")),
            new InlineOption(VaginalExperience.Forceps, this.getString("ForcepsLabel")),
            new InlineOption(VaginalExperience.VaginalBirthAfterCaesarianSection, this.getString("VaginalBirthAfterCesareanAbbrevLabel")),
            new InlineOption(VaginalExperience.EpisiotomyOrLacerations, this.getString("EpisiotomyOrLacerationsLabel")),
            new InlineOption(VaginalExperience.ObstetricalAnalSphincterInjuries, this.getString("ObstetricalAnalSphincterInjuriesAbbreviationLabel"))
        ];
    }

    public get caesareanBirthReviewOptions(): InlineOption<ActivityPlanning>[] {
        return [
            new InlineOption(ActivityPlanning.Planned, this.getString("PlannedLabel")),
            new InlineOption(ActivityPlanning.Unplanned, this.getString("UnplannedLabel"))
        ];
    }

    public get infantFeedingTypeOptions(): InlineOption<InfantFeedingType>[] {
        return [
            new InlineOption(InfantFeedingType.BreastMilkOnly, this.getString("ObstetricalFile_BreastMilkOnlyLabel")),
            new InlineOption(InfantFeedingType.CombinationOfBreastMilkAndSubstitute, this.getString("ObstetricalFile_CombinationOfBreastMilkAndSubstituteLabel")),
            new InlineOption(InfantFeedingType.BreastMilkSubstituteOnly, this.getString("ObstetricalFile_BreastMilkSubstituteOnlyLabel"))
        ];
    }
}

export { OntarioObstetricalFilePostnatalHistoryInfant };
